import AccessLevelIcon from '@account/components/AccountList/AccessLevelIcon'
import { ArrowRightSimpleIcon, ChevronRightIcon, CurrencyIcon, MMButton, ProCard } from '@components'
import type { PreSelectedAccountState } from '@hooks'
import cn from '@integrations/classnames'
import { type KeyboardEvent, useEffect, useRef, useState } from '@integrations/react'
import { useNavigate } from '@integrations/routing'
import { uiSize } from '@utils'
import { paths, useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, MerchantAccessLevel, Nullable } from 'mmfintech-backend-api/types'
import { formatMoney, tr } from 'mmfintech-commons'
import styles from './StaffAccountCards.module.scss'

/** Pixels the mouse must travel before a press counts as a drag rather than a click. */
const DRAG_THRESHOLD = 5

function preventDefault(event: Event) {
  event.preventDefault()
}

interface Props {
  accounts: AccountBalanceResponse[]
  getAccessLevel: (accountId: number) => Nullable<MerchantAccessLevel>
}

/** One blue card per account the staff member can access, in a single row the arrows slide through. */
function StaffAccountCards({ accounts, getAccessLevel }: Props) {
  const { getCurrencyPrecision } = useCurrencies()
  const navigate = useNavigate()

  const trackRef = useRef<HTMLDivElement>(null)
  const [canSlide, setCanSlide] = useState({ prev: false, next: false })

  // biome-ignore lint/correctness/useExhaustiveDependencies: `accounts` is the trigger — re-measure when cards change
  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    // Runs on every scroll frame: re-render only when an arrow appears or disappears
    function measure() {
      const prev = track.scrollLeft > 1
      const next = track.scrollLeft + track.clientWidth < track.scrollWidth - 1
      setCanSlide((current) => (current.prev === prev && current.next === next ? current : { prev, next }))
    }

    measure()
    track.addEventListener('scroll', measure, { passive: true })
    window.addEventListener('resize', measure)
    return () => {
      track.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
    }
  }, [accounts])

  // The whole card is a target and opens the Accounts page on that account; the arrow keeps its own destination.
  function openAccounts(accountId: number) {
    navigate(paths.banking.accounts.list(), {
      state: { preSelectedAccount: accountId } satisfies PreSelectedAccountState
    })
  }

  // Mouse drag scrolls the row like a swipe does on a touch screen. Touch keeps its native scrolling.
  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    let startX = 0
    let startScroll = 0
    let dragged = false

    function handleMove(event: MouseEvent) {
      const distance = event.clientX - startX
      if (!dragged && Math.abs(distance) > DRAG_THRESHOLD) {
        dragged = true
        track.dataset.dragging = 'true'
        // Snapping would pull the row to a card edge on release; after a drag it stays exactly where it was let go.
        track.dataset.free = 'true'
      }
      if (dragged) track.scrollLeft = startScroll - distance
    }

    function handleUp() {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
      if (!dragged) return

      delete track.dataset.dragging
      // The click that ends a drag must not open the card; the timer clears the flag if no click follows.
      setTimeout(() => {
        dragged = false
      }, 0)
    }

    function handleDown(event: MouseEvent) {
      if (event.button !== 0) return
      startX = event.clientX
      startScroll = track.scrollLeft
      dragged = false
      window.addEventListener('mousemove', handleMove)
      window.addEventListener('mouseup', handleUp)
    }

    function swallowClickAfterDrag(event: MouseEvent) {
      if (!dragged) return
      event.stopPropagation()
      event.preventDefault()
    }

    track.addEventListener('mousedown', handleDown)
    track.addEventListener('click', swallowClickAfterDrag, true)
    track.addEventListener('dragstart', preventDefault)
    return () => {
      track.removeEventListener('mousedown', handleDown)
      track.removeEventListener('click', swallowClickAfterDrag, true)
      track.removeEventListener('dragstart', preventDefault)
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [])

  // Enter / Space open the card, the arrow keys walk the row (focus scrolls the card into view). Keys that
  // belong to the arrow button inside the card are left to it.
  function handleCardKeyDown(event: KeyboardEvent<HTMLElement>, accountId: number) {
    if (event.target !== event.currentTarget) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openAccounts(accountId)
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault()
      const sibling =
        event.key === 'ArrowRight' ? event.currentTarget.nextElementSibling : event.currentTarget.previousElementSibling
      ;(sibling as HTMLElement | null)?.focus()
    }
  }

  // One card at a time, so the next account always lands where the reader is looking.
  function slide(direction: 1 | -1) {
    const track = trackRef.current
    const card = track?.firstElementChild as HTMLElement | null
    if (!card) return

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' })
  }

  return (
    <section
      className={cn(styles['staff-account-cards'], 'staff-account-cards')}
      aria-label={tr('FRONTEND.DASHBOARD.STAFF.YOUR_ACCOUNTS', 'Your accounts')}
      data-test='staff-account-cards'
    >
      <div
        className={cn(styles['staff-account-cards__track'], 'staff-account-cards__track')}
        data-fade-start={canSlide.prev || undefined}
        data-fade-end={canSlide.next || undefined}
        ref={trackRef}
      >
        {accounts.map(({ id, name, currencyCode, balance }) => (
          <ProCard
            tone='brand'
            className={cn(styles['staff-account-card'], 'staff-account-card')}
            key={id}
            onClick={() => openAccounts(id)}
            onKeyDown={(event) => handleCardKeyDown(event, id)}
            tabIndex={0}
            role='link'
            aria-label={`${name}, ${tr('FRONTEND.DASHBOARD.STAFF.OPEN_ACCOUNTS', 'Open accounts')}`}
            data-test='staff-account-card'
          >
            <div className={cn(styles['staff-account-card__head'], 'staff-account-card__head')}>
              <CurrencyIcon currency={currencyCode} size={uiSize(20)} circle />
              <span className={cn(styles['staff-account-card__name'], 'staff-account-card__name')} title={name}>
                {name}
              </span>
              <AccessLevelIcon level={getAccessLevel(id)} />
            </div>

            <div className={cn(styles['staff-account-card__balance-row'], 'staff-account-card__balance-row')}>
              <div
                className={cn(styles['staff-account-card__balance'], 'staff-account-card__balance')}
                data-test='staff-account-balance'
              >
                {formatMoney(balance, '', getCurrencyPrecision(currencyCode)).trim()}
                <span className={cn(styles['staff-account-card__currency'], 'staff-account-card__currency')}>
                  {currencyCode}
                </span>
              </div>

              <MMButton
                variant='primary'
                collapsed
                collapsedIcon={ArrowRightSimpleIcon}
                title={tr('FRONTEND.ACCOUNTS.COLUMNS.VIEW_TRANSACTIONS', 'View transactions')}
                aria-label={tr('FRONTEND.ACCOUNTS.COLUMNS.VIEW_TRANSACTIONS', 'View transactions')}
                onClick={(event) => {
                  event.stopPropagation()
                  navigate(paths.banking.transactions.listByAccountId(id))
                }}
                data-test='staff-account-view-transactions'
              />
            </div>
          </ProCard>
        ))}
      </div>

      {canSlide.prev && (
        <span
          className={cn(
            styles['staff-account-cards__arrow'],
            styles['staff-account-cards__arrow--prev'],
            'staff-account-cards__arrow'
          )}
        >
          <MMButton
            variant='dashboard'
            collapsed
            collapsedIcon={
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4'>
                <path d='M15 6l-6 6 6 6' />
              </svg>
            }
            title={tr('FRONTEND.DASHBOARD.STAFF.PREVIOUS_ACCOUNTS', 'Previous accounts')}
            aria-label={tr('FRONTEND.DASHBOARD.STAFF.PREVIOUS_ACCOUNTS', 'Previous accounts')}
            onClick={() => slide(-1)}
            data-test='staff-accounts-prev'
          />
        </span>
      )}
      {canSlide.next && (
        <span
          className={cn(
            styles['staff-account-cards__arrow'],
            styles['staff-account-cards__arrow--next'],
            'staff-account-cards__arrow'
          )}
        >
          <MMButton
            variant='dashboard'
            collapsed
            collapsedIcon={<ChevronRightIcon size={null} strokeWidth='2.4' />}
            title={tr('FRONTEND.DASHBOARD.STAFF.NEXT_ACCOUNTS', 'Next accounts')}
            aria-label={tr('FRONTEND.DASHBOARD.STAFF.NEXT_ACCOUNTS', 'Next accounts')}
            onClick={() => slide(1)}
            data-test='staff-accounts-next'
          />
        </span>
      )}
    </section>
  )
}

export default StaffAccountCards
